import { assertCUID, isCUID } from "@esgt/cuid"
import type { AnswerObject } from "@esgt/event-store"
import type { CUID, Question } from "@esgt/types"

const findQuestion = (questionId: string, questionsById: Record<CUID, Question>) => {
  try {
    assertCUID(questionId)
    return questionsById[questionId]
  } catch {
    return
  }
}

/**
 * Checks whether a question's dependency requirements are not met by walking
 * up the ancestor chain. Unlike `isNoOrDisabledByDependency`, this function
 * does NOT consider whether a question answered "No" on its own — it only
 * checks whether the chain of `dependsOn` relationships is broken.
 *
 * This is used when recursing into ancestors so that a parent that is
 * legitimately answered "No" (which activates a `dependsOnValue: "false"`
 * child) is not mistakenly treated as disabled.
 */
function isDependencyChainBroken(
  question: Question,
  questionsById: Record<CUID, Question>,
  answerValues: Record<string, AnswerObject>,
): boolean {
  if (!question.dependsOn) return false

  const requiredParentAnswer = question.dependsOnValue ?? "true"
  const parentAnswer = answerValues[question.dependsOn]?.primaryAnswer

  if (parentAnswer !== undefined && parentAnswer !== requiredParentAnswer) {
    return true
  }

  const parent = findQuestion(question.dependsOn, questionsById)
  if (parent) {
    return isDependencyChainBroken(parent, questionsById, answerValues)
  }

  return false
}

/**
 * Determines whether a question should be treated as disabled — either
 * because it has been answered "No" itself, or because its dependency chain
 * is broken (an ancestor's answer does not satisfy the required value).
 *
 * Supports `dependsOnValue` to invert the dependency: when set to `"false"`,
 * the child is only active when the parent is answered "No". Ancestor
 * checking uses `isDependencyChainBroken` so that a parent's own "No" answer
 * does not incorrectly disable its "No-branch" children.
 */
export function isNoOrDisabledByDependency(
  answerScope: CUID | string,
  questionsById: Record<CUID, Question>,
  answerValues: Record<string, AnswerObject>,
): boolean {
  // Answered "No" on the question itself — treated as disabled for
  // downstream datapoint/upload purposes.
  if (answerValues[answerScope]?.primaryAnswer === "false") {
    return true
  }

  const question = findQuestion(answerScope, questionsById)

  if (isCUID(answerScope) && question) {
    return isDependencyChainBroken(question, questionsById, answerValues)
  }

  return false
}
